import { useState, useMemo } from "react";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Users, Calculator, Truck, Loader2, Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

const CustomerSection = ({
  customers,
  loadingCustomers,
  selectedCustomer,
  onCustomerSelect,
  propertyAddress,
  onPropertyAddressChange,
  onCalculateDistance,
  calculatingDistance,
  baseLocation,
  distanceInfo,
}) => {
  const [open, setOpen] = useState(false);

  // Customers come from the API pre-sorted by Airtable createdTime desc (most recent first).
  // Preserve that order in the dropdown.
  const sortedCustomers = useMemo(() => customers || [], [customers]);

  const handleSelect = (customerId) => {
    onCustomerSelect(customerId);
    setOpen(false);
  };

  return (
    <>
      <CardHeader className="border-b border-zinc-100 px-6 py-4">
        <CardTitle className="font-heading text-lg uppercase tracking-tight text-[#00341d] flex items-center gap-2">
          <Users className="w-5 h-5" />
          Customer Information
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div>
          <Label className="text-sm font-medium text-zinc-700 mb-2 block">Select Customer from Airtable</Label>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full h-11 justify-between bg-zinc-50/50 font-normal"
                disabled={loadingCustomers}
                data-testid="customer-select"
              >
                {loadingCustomers ? (
                  "Loading..."
                ) : selectedCustomer ? (
                  <span className="truncate">
                    {selectedCustomer.name} {selectedCustomer.company && `- ${selectedCustomer.company}`}
                  </span>
                ) : (
                  "Search or select a customer..."
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Type to search customers..." />
                <CommandList>
                  <CommandEmpty>No customer found.</CommandEmpty>
                  <CommandGroup>
                    {sortedCustomers.map((customer) => (
                      <CommandItem
                        key={customer.id}
                        value={`${customer.name} ${customer.company || ""} ${customer.email || ""}`}
                        onSelect={() => handleSelect(customer.id)}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedCustomer?.id === customer.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex flex-col">
                          <span>{customer.name} {customer.company && <span className="text-zinc-500">- {customer.company}</span>}</span>
                          {customer.email && <span className="text-xs text-zinc-400">{customer.email}</span>}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {selectedCustomer && (
          <div className="bg-zinc-50 rounded-lg p-4 space-y-2">
            <p className="font-medium text-[#00341d]">{selectedCustomer.name}</p>
            {selectedCustomer.email && <p className="text-sm text-zinc-600">{selectedCustomer.email}</p>}
            {selectedCustomer.phone && <p className="text-sm text-zinc-600">{selectedCustomer.phone}</p>}
          </div>
        )}

        <div>
          <Label className="text-sm font-medium text-zinc-700 mb-2 block">Property Address</Label>
          <div className="flex gap-2">
            <Input
              value={propertyAddress}
              onChange={(e) => onPropertyAddressChange(e.target.value)}
              placeholder="Enter property address"
              className="h-11 bg-zinc-50/50 flex-1"
              name="lcc-prop-loc"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
              data-form-type="other"
              data-testid="property-address-input"
            />
            <Button
              type="button"
              onClick={() => onCalculateDistance(propertyAddress)}
              disabled={calculatingDistance || !propertyAddress}
              className="h-11 bg-[#d97706] hover:bg-[#b45309] text-white px-4"
              data-testid="calculate-distance-button"
            >
              {calculatingDistance ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <><Calculator className="w-4 h-4 mr-2" />Calc Distance</>
              )}
            </Button>
          </div>
          {baseLocation && (
            <p className="text-xs text-zinc-500 mt-2">
              <Truck className="w-3 h-3 inline mr-1" />
              Base: {baseLocation.address} | ${baseLocation.rate_per_mile}/mile round trip
            </p>
          )}
        </div>

        {distanceInfo && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Truck className="w-5 h-5 text-amber-700" />
              <span className="font-semibold text-amber-800">Distance Calculated</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-amber-600">One Way</p>
                <p className="font-bold text-amber-900">{distanceInfo.distance_miles} mi</p>
              </div>
              <div>
                <p className="text-amber-600">Round Trip</p>
                <p className="font-bold text-amber-900">{distanceInfo.round_trip_miles} mi</p>
              </div>
              <div>
                <p className="text-amber-600">Mobilization</p>
                <p className="font-bold text-amber-900">${distanceInfo.mobilization_fee.toFixed(2)}</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </>
  );
};

export default CustomerSection;
